'use client';
import React, { useState, createContext, useContext, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Plus, Heart, Globe, ChevronDown, LogIn, Store, Building2, Shield } from 'lucide-react';
import dynamic from 'next/dynamic';



const DonateModal = dynamic(() => import('@/app/interactive-map/components/DonateModal'), { ssr: false });

// ─── Language Context ─────────────────────────────────────────────────────────
export type AppLanguage = 'en' | 'tl' | 'ceb';

interface LanguageContextType {
  language: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
  t: (key: string) => string;
}

const translations: Record<AppLanguage, Record<string, string>> = {
  en: {
    maps: 'Maps',
    admin: 'Admin',
    donate: 'Donate',
    addPantry: 'Add a Community Pantry',
    language: 'Language',
    english: 'English',
    tagalog: 'Tagalog',
    bisaya: 'Bisaya',
  },
  tl: {
    maps: 'Mapa',
    admin: 'Admin',
    donate: 'Mag-donate',
    addPantry: 'Magdagdag ng Community Pantry',
    language: 'Wika',
    english: 'Ingles',
    tagalog: 'Tagalog',
    bisaya: 'Bisaya',
  },
  ceb: {
    maps: 'Mapa',
    admin: 'Admin',
    donate: 'Mag-donate',
    addPantry: 'Pagdugang og Community Pantry',
    language: 'Pinulongan',
    english: 'Iningles',
    tagalog: 'Tagalog',
    bisaya: 'Bisaya',
  },
};

export const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

export function useLanguage() {
  return useContext(LanguageContext);
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('pantrymap_lang') as AppLanguage) || 'en';
    }
    return 'en';
  });

  const setLanguage = (lang: AppLanguage) => {
    setLanguageState(lang);
    if (typeof window !== 'undefined') {
      localStorage.setItem('pantrymap_lang', lang);
    }
  };

  const t = (key: string) => translations[language][key] ?? key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// ─── Login Role Modal ─────────────────────────────────────────────────────────
function LoginRoleModal({ onClose }: { onClose: () => void }) {
  const router = useRouter();

  function handleSelect(role: 'owner' | 'hub' | 'admin') {
    onClose();
    if (role === 'owner') {
      router.push('/pantry-owner/login');
    } else if (role === 'hub') {
      router.push('/hub-coordinator/login');
    } else {
      router.push('/admin-panel');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-[#1e5c38]">Mag-login bilang…</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f0f2f5] transition-colors">
            <X size={18} className="text-[#6b7280]" />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={() => handleSelect('owner')}
            className="flex items-center gap-4 p-4 rounded-xl border-2 border-[#d1ead9] hover:border-[#2d7a4f] hover:bg-[#f0faf4] transition-all group text-left"
          >
            <div className="w-12 h-12 rounded-full bg-[#e8f5ee] flex items-center justify-center flex-shrink-0 group-hover:bg-[#d1ead9] transition-colors">
              <Store size={22} className="text-[#2d7a4f]" />
            </div>
            <div>
              <p className="font-semibold text-[#1e5c38] text-sm">Community Pantry Owner</p>
              <p className="text-xs text-[#6b7280] mt-0.5">I-manage ang iyong pantry at mag-post ng updates</p>
            </div>
          </button>

          <button
            onClick={() => handleSelect('hub')}
            className="flex items-center gap-4 p-4 rounded-xl border-2 border-[#dbeafe] hover:border-[#3b82f6] hover:bg-[#eff6ff] transition-all group text-left"
          >
            <div className="w-12 h-12 rounded-full bg-[#dbeafe] flex items-center justify-center flex-shrink-0 group-hover:bg-[#bfdbfe] transition-colors">
              <Building2 size={22} className="text-[#2563eb]" />
            </div>
            <div>
              <p className="font-semibold text-[#1e40af] text-sm">Donation Hub Coordinator</p>
              <p className="text-xs text-[#6b7280] mt-0.5">I-manage ang donation hub at i-track ang mga donasyon</p>
            </div>
          </button>

          <button
            onClick={() => handleSelect('admin')}
            className="flex items-center gap-4 p-4 rounded-xl border-2 border-[#e5e7eb] hover:border-[#6b7280] hover:bg-[#f9fafb] transition-all group text-left"
          >
            <div className="w-12 h-12 rounded-full bg-[#f3f4f6] flex items-center justify-center flex-shrink-0 group-hover:bg-[#e5e7eb] transition-colors">
              <Shield size={22} className="text-[#4b5563]" />
            </div>
            <div>
              <p className="font-semibold text-[#374151] text-sm">Admin</p>
              <p className="text-xs text-[#6b7280] mt-0.5">I-manage ang lahat ng pantry at submissions</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Topbar ───────────────────────────────────────────────────────────────────
const LANGUAGE_OPTIONS: { value: AppLanguage; label: string; key: string }[] = [
  { value: 'en', label: 'English', key: 'english' },
  { value: 'tl', label: 'Tagalog', key: 'tagalog' },
  { value: 'ceb', label: 'Bisaya', key: 'bisaya' },
];

export default function Topbar() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  // Listen for custom event from WelcomeModal
  React.useEffect(() => {
    const handler = () => setLoginModalOpen(true);
    window.addEventListener('open-login-modal', handler);
    return () => window.removeEventListener('open-login-modal', handler);
  }, []);

  const currentLangLabel = LANGUAGE_OPTIONS.find((o) => o.value === language)?.label ?? 'English';

  return (
    <>
      <header className="sticky top-0 z-50 bg-white border-b border-[#d1ead9] shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/interactive-map" className="flex items-center gap-2.5 group">
            <AppLogo size={36} />
            <div className="flex flex-col leading-none">
              <span className="text-xl font-700 tracking-tight text-[#1e5c38] group-hover:text-[#2d7a4f] transition-colors font-semibold leading-tight">
                TulongTayo
              </span>
              <span className="text-[9px] font-light tracking-widest text-[#6b7280] uppercase leading-tight mt-0.5">
                Find · Donate · Volunteer
              </span>
            </div>
          </Link>

          {/* CTA Buttons + Mobile Menu */}
          <div className="flex items-center gap-2">
            {/* Find Button — desktop */}
            <Link
              href="/interactive-map"
              className="hidden sm:flex items-center gap-1.5 bg-white hover:bg-[#f0faf4] border border-[#d1ead9] text-[#4b5563] text-sm font-semibold px-3 py-2 rounded-lg transition-all duration-150"
            >
              Find
            </Link>

            {/* Donate Button — desktop */}
            <button
              onClick={() => setDonateOpen(true)}
              className="hidden sm:flex items-center gap-1.5 bg-[#f0faf4] hover:bg-[#e8f5ee] border border-[#d1ead9] text-[#2d7a4f] text-sm font-semibold px-3 py-2 rounded-lg transition-all duration-150"
            >
              <Heart size={14} />
              {t('donate')}
            </button>

            {/* Add a Community Pantry — desktop */}
            <Link
              href="/submit-a-pantry"
              className="hidden md:flex items-center gap-1.5 bg-[#2d7a4f] hover:bg-[#1e5c38] text-white text-sm font-semibold px-3 py-2 rounded-lg transition-all duration-150"
            >
              <Plus size={14} />
              {t('addPantry')}
            </Link>

            {/* Login Button — desktop */}
            <button
              onClick={() => setLoginModalOpen(true)}
              className="hidden sm:flex items-center gap-1.5 bg-white hover:bg-[#f0faf4] border border-[#d1ead9] text-[#4b5563] text-sm font-semibold px-3 py-2 rounded-lg transition-all duration-150"
            >
              <LogIn size={14} />
              Login
            </button>

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 rounded-lg text-[#4b5563] hover:bg-[#f0faf4] transition-colors"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMenuOpen(false)}>
          <div
            className="absolute top-16 left-0 right-0 bg-white border-b border-[#d1ead9] shadow-lg p-4 flex flex-col gap-1"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex flex-col gap-2">
              {/* Find in hamburger */}
              <Link
                href="/interactive-map"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 border border-[#d1ead9] text-[#4b5563] text-sm font-semibold px-4 py-3 rounded-lg w-full hover:bg-[#f0faf4] transition-all"
              >
                Find
              </Link>

              {/* Donate in hamburger */}
              <button
                onClick={() => { setMenuOpen(false); setDonateOpen(true); }}
                className="flex items-center justify-center gap-2 border border-[#d1ead9] text-[#2d7a4f] text-sm font-semibold px-4 py-3 rounded-lg w-full hover:bg-[#f0faf4] transition-all"
              >
                <Heart size={16} />
                {t('donate')}
              </button>

              <Link
                href="/submit-a-pantry"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 bg-[#2d7a4f] text-white text-sm font-semibold px-4 py-3 rounded-lg w-full"
              >
                <Plus size={16} />
                {t('addPantry')}
              </Link>

              {/* Login in hamburger */}
              <button
                onClick={() => { setMenuOpen(false); setLoginModalOpen(true); }}
                className="flex items-center justify-center gap-2 border border-[#d1ead9] text-[#4b5563] text-sm font-semibold px-4 py-3 rounded-lg w-full hover:bg-[#f0faf4] transition-all"
              >
                <LogIn size={16} />
                Login
              </button>

              {/* Language Selector */}
              <div className="relative mt-1">
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 rounded-lg border border-[#d1ead9] text-sm font-medium text-[#4b5563] hover:bg-[#f0faf4] transition-all"
                >
                  <span className="flex items-center gap-2">
                    <Globe size={16} className="text-[#2d7a4f]" />
                    {t('language')}: <span className="font-semibold text-[#2d7a4f]">{currentLangLabel}</span>
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-[#6b7280] transition-transform duration-200 ${langDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {langDropdownOpen && (
                  <div className="mt-1 bg-white border border-[#d1ead9] rounded-lg shadow-md overflow-hidden">
                    {LANGUAGE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setLanguage(opt.value);
                          setLangDropdownOpen(false);
                        }}
                        className={`flex items-center justify-between w-full px-4 py-2.5 text-sm transition-colors ${
                          language === opt.value
                            ? 'bg-[#e8f5ee] text-[#2d7a4f] font-semibold'
                            : 'text-[#374151] hover:bg-[#f0faf4]'
                        }`}
                      >
                        {opt.label}
                        {language === opt.value && (
                          <span className="w-2 h-2 rounded-full bg-[#2d7a4f]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Donate Modal */}
      {donateOpen && <DonateModal onClose={() => setDonateOpen(false)} />}

      {loginModalOpen && <LoginRoleModal onClose={() => setLoginModalOpen(false)} />}
    </>
  );
}